(ns arthur.db
  "app-db: authored data and ids. Nothing derived, and nothing large.

  That sounds like hygiene and it is the precondition for two things that are
  otherwise unbuildable — spec validation on every event, which is only
  affordable over authored data, and cheap writes, since every edit `assoc`es
  into this map and every mounted layer-2 sub compares the result.

  So the clip is here (it is a document — a human placed every node) and dense
  channel blocks are not; they live behind a handle in `store`. The hand-written
  demo clip has no dense blocks and its store is empty; the swarm and the take
  are entirely dense."
  (:require [arthur.demo :as demo]
            [arthur.domain.clip :as domain-clip]
            [arthur.demo.swarm :as swarm]
            [arthur.demo.take :as take]))

(defn- entry
  "A clip plus what the transport and the stage read off it.

  Read OFF the clip rather than written again beside it: copying a number by hand
  into this table is how it comes to disagree with the document it describes.
  There is no `:frames` here, because a length belongs to a symbol and which
  symbol is open is the editor's state — see `events/playback/frames`."
  [label-key label clip store]
  (merge {:label label :clip clip :store store
          ;; A static asset since step 9, and not the repo root's `audio.wav`.
          ;; That file is `extract.sh`'s output — tier 3, which the backend now
          ;; serves by hash — and the synthetic take needs a sound of its own so
          ;; that the clock has something to run against with no footage ingested.
          :audio "/static/arthur/audio.wav"
          :cid (name label-key)}
         (select-keys clip [:fps :width :height])))

(def clips
  "The hand-made clips, selectable from the transport.

  `:swarm` is the load test: a hundred and twenty nodes, entirely dense. The two
  takes are step 5's deliverable and they are ONE freeze — the same blocks, with
  `:head` written as a dense track in one and as framed identity in the other, so
  the button that switches between them switches a document field and nothing
  else."
  {:demo        {:label "demo"   :entry (delay (entry :demo "demo" demo/clip nil))}
   :swarm       {:label "swarm"  :entry (delay (entry :swarm "swarm" @swarm/clip @swarm/store))}
   :take        {:label "take"   :entry (delay (entry :take "take" @take/clip @take/store))}
   :take-locked {:label "locked" :entry (delay (entry :take-locked "locked" @take/locked @take/store))}})

(defn clip-entry [id]
  (some-> (get-in clips [id :entry]) deref))

(def tracing
  "How tracing layers show on the stage: all of them or none, how strongly, and
  which ones are switched off, as `[symbol-id node-id]` — the symbol a tracing
  placement is in and its id, so a face's footage is one layer wherever the face
  is placed.

  THE EDITOR'S, NOT THE DOCUMENT'S. Showing a reference is a way of looking at
  the stage, like solo and zoom: not an undo step, not sent to collaborators,
  and it cannot reach an export. A layer is shown unless it is in `:hidden`, so
  footage brought in shows without being found and switched on first — once
  tracing itself is on, which it is not until asked for."
  {:on? false :opacity 0.5 :hidden #{}})

(def default
  {;; --- the document ---
   ;;
   ;; NOTHING IS LOADED. `core/init` dispatches `::project/new` before the first
   ;; render, so the app opens on a blank stage rather than on whichever built-in
   ;; scene happened to be convenient — the demos, the swarm and the two takes are
   ;; rows in the media pool like anything else, and reference material is not a
   ;; default. The values below are what a blank document is; they are replaced by
   ;; that dispatch and exist so this map is a valid db on its own.
   :clip/current nil
   :paint/revision 0
   :palette :arthur/default          ; a NAME; the ramp itself is project data

   ;; --- the clip ---
   ;;
   ;; Including the STAGE DIMENSIONS, which are the project's and not the
   ;; footage's. That is what deleting `makeXform` buys — the framing became a
   ;; transform on a node, so nothing downstream of the freeze knows the frame
   ;; size — and it is why ui/player no longer hardcodes 320x200.
   :clip    (let [c (domain-clip/blank)]
              {:fps (:fps c)
               :width (:width c) :height (:height c)
               :audio nil})

   ;; Which ingested footage to detect, and what the last load said. The list
   ;; comes from the server — tier 3 is the backend's since step 9 — so there is
   ;; no path to type any more.
   :footage {:id nil :label nil :loading? false :status nil
             :available [] :chosen nil :uploaded #{}}

   ;; Every symbol in every saved project, for the pool's all-assets folder. Rows
   ;; from `/api/symbols`, nothing loaded: a symbol from elsewhere is fetched when
   ;; it is dropped.
   :assets {:symbols [] :palettes [] :loading? false}

   ;; The document's own identity on the server. `:seq` is the monotonic project
   ;; version: a client that sees a delta with `seq > local + 1` refetches, which
   ;; is what will make staleness self-healing once there is a broadcast to miss.
   :project {:id nil :cid nil :name nil :seq nil :busy? false :status nil}

   ;; What the server holds, for the open menu. A list of rows and nothing more —
   ;; opening one fetches the document itself.
   :projects {:items [] :loading? false}

   ;; --- transport ---
   ;;
   ;; The playhead is in app-db like everything else. An earlier draft of
   ;; docs/architecture.md put it in a standalone ratom to dodge an invalidation
   ;; storm that does not happen: with layer-2 extractors and layer-3
   ;; computations, a tick re-runs one cheap extractor per mounted sub, each
   ;; returning the same value for every subtree the tick did not touch, and
   ;; therefore notifying nobody. ::resolver does not re-run.
   ;;
   ;; Two reasons it belongs here rather than outside: a seek in the event log is
   ;; how scrubbing becomes inspectable in re-frame-10x, and a collaborator's
   ;; playhead is a feature — putting it outside app-db puts it outside the
   ;; machinery that would share it.
   ;; --- export ---
   ;;
   ;; The REQUEST and its progress, never the frames. Which symbol to write and
   ;; at what integer zoom is authored state like anything else; the megabytes the
   ;; render produces are handed straight to a download and never enter the db.
   ;; `:isolate` is the placement to render alone, or nil for the whole symbol;
   ;; `:symbol` nil means whichever symbol is open.
   :export  {:symbol nil :isolate nil :zoom 4 :busy? false :done 0 :total 0
             :status nil}

   :playback {:frame    0
              :playing? false
              :rate     1.0
              ;; Both for profiling: loop so a run at 4x lasts longer than the
              ;; clip, mute so sitting in one does not require enduring it.
              :loop?    false
              :muted?   false}

   ;; --- the editor's own state ---
   ;;
   ;; IN app-db, not in ratoms beside the components that read it. What is
   ;; selected is asked by four panes at once — the params pane renders it, the
   ;; timeline highlights its row, the stage draws its handles, the palette says
   ;; which tone a new shape gets — and a `defonce` atom private to one namespace
   ;; can only be shared by making the other three require that namespace for its
   ;; state. It is also small and authored, which is the bar `arthur.db` sets.
   ;;
   ;; `:selection` is a vector whose first element says what kind of thing it
   ;; names, so a pane dispatches on it rather than on which of several
   ;; "selected-x" keys happens to be non-nil:
   ;;
   ;;   [:node <symbol> <node>]     a shape or an instance
   ;;   [:symbol <id>]              a symbol
   ;;   [:subject <id>] [:feature <id>] [:group <id>]   a tracked object
   ;;
   ;; `:draft` is the polygon being clicked out, flat [x y x y …] as geometry is
   ;; stored everywhere. `:expanded` holds timeline row PATHS — a path and not a
   ;; node id, because one symbol placed twice is two rows that open separately.
   ;;
   ;; `:open` is the symbol on screen — the one the stage draws, the timeline
   ;; lists, the transport plays and a new shape goes into — and `:tabs` the
   ;; symbols open beside it. Editor state and not the document's, because no
   ;; symbol is special to the document: which one you are looking at is a fact
   ;; about you.
   ;;
   ;; `:knobs` holds a generated setting's value WHILE THE REGENERATION IS IN
   ;; FLIGHT, keyed by [scope id knob]. Moving a slider dispatches a preview that
   ;; re-freezes blocks asynchronously, so until it lands the clip still reports
   ;; the old value — and a slider reading from the clip would spring back under
   ;; the user's finger on every frame of the drag.
   :ui {:open      nil
        :tabs      []
        :selection nil
        :selections []
        :tone      1
        :tool      :select
        :brush     6
        :auto-key? false
        :draft     []
        :knobs     {}
        :tracing   tracing
        :expanded  #{}}})

(def rates
  "The transport's rates — all of them `playbackRate` on the audio element, so
  the picture cannot drift from the sound at any of them.

  2x and 4x are there to be profiled at rather than watched. A 30fps clip at 2x
  wants sixty clip frames a second against a 60Hz display, so every animation
  frame has to paint a new one: it is the point where the loop stops having
  slack. Past that the clock starts dropping frames rather than falling behind,
  which is the whole reason the frame is derived from the audio instead of
  counted — and the transport reports the drop rate so that it is visible rather
  than merely survivable."
  [0.25 0.5 1.0 2.0 4.0])
